import { MyShift, Plantao } from "../models/Plantao";
import getClient from "./ApiClient";
import { readItem, readItems, createItem, updateItem, updateItems, deleteItem, deleteItems } from "@directus/sdk";
import { ShiftStatus } from "../types/shiftStatus";
import { ShiftMode } from "../types/shiftMode";

const shift_collection_name = "shifts";
const shift_team_collection_name = "shifts_team";

export type ShiftPayload = {
  mode: ShiftMode;
  status: ShiftStatus;
  start_date_utc: string;
  end_date_utc: string;
  city_id?: string | null;
  language_id?: string | null;
  facilitator_user_id?: string | null;
  total_slots?: number;
};

export type ShiftTeamPayload = {
  shift_id?: string;
  interviewer_user_id?: string | null;
  assistant_user_id?: string | null;
  start_date_utc: string;
  end_date_utc: string;
};

function getUserIdFromRelation(value: any): string | null {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof value.id === "string") return value.id;
  return null;
}

function normalizeShiftMode(mode?: ShiftMode | string) {
  if (!mode) return undefined;
  const normalized = mode.toString().trim().toLowerCase();

  if (normalized === "online") return "Online";
  if (normalized === "presencial" || normalized === "presential") return "Presential";

  return mode;
}

export async function getShifts(status: ShiftStatus[], mode?: ShiftMode, placeId?: string) {
  const clientInstance = await getClient();
  const apiMode = normalizeShiftMode(mode);

  return clientInstance.request(readItems(shift_collection_name, {
    fields: ["*.*"],
    filter: {
      status: {
        _in: status
      },
      end_date_utc: {
        _gte: "$NOW"
      },
      ...(apiMode ? { mode: { _eq: apiMode } } : {}),
      ...(placeId ? { place_id: { _eq: placeId } } : {})
    },
    sort: ["start_date_utc"]
  }));
}

export async function getShift(id: string): Promise<Plantao> {
  const clientInstance = await getClient();
  return clientInstance.request<Plantao>(readItem(shift_collection_name, id, {
    fields: ["*.*.*"]
  }));
}

export async function createShift(data: ShiftPayload) {
  const clientInstance = await getClient();
  return clientInstance.request(createItem("shifts", data));
}

export async function updateShift(id: string, data: Partial<ShiftPayload>) {
  const clientInstance = await getClient();
  return clientInstance.request(updateItem("shifts", id, data));
}

export async function deleteShift(id: string) {
  const clientInstance = await getClient();

  // Remove dependent team entries first to avoid FK constraint failures.
  const teamRows = await clientInstance.request(
    readItems(shift_team_collection_name, {
      fields: ["id"],
      filter: {
        shift_id: {
          _eq: id
        }
      },
      limit: -1
    })
  );

  const teamIds = (teamRows as Array<{ id?: string | number }>)
    .map((row) => row?.id)
    .filter((value): value is string | number => value !== undefined && value !== null)
    .map((value) => String(value));

  if (teamIds.length > 0) {
    await clientInstance.request(deleteItems(shift_team_collection_name, teamIds));
  }

  return clientInstance.request(deleteItem("shifts", id));
}

export async function createShiftTeamEntry(data: ShiftTeamPayload) {
  const clientInstance = await getClient();
  return clientInstance.request(createItem("shifts_team", data));
}

export async function updateShiftTeamEntry(id: string, data: Omit<ShiftTeamPayload, "shift_id">) {
  const clientInstance = await getClient();
  return clientInstance.request(updateItem("shifts_team", id, data));
}

export async function deleteShiftTeamEntry(id: string) {
  const clientInstance = await getClient();
  return clientInstance.request(deleteItem("shifts_team", id));
}

export async function updateShiftInterviewer(shift_team_id: string, user_id: string) {
  const clientInstance = await getClient();
  await clientInstance.request(updateItems(shift_team_collection_name, [shift_team_id], { interviewer_user_id: user_id }));
}

export async function updateShiftAssistant(shift_team_id: string, user_id: string) {
  const clientInstance = await getClient();
  await clientInstance.request(updateItems(shift_team_collection_name, [shift_team_id], { assistant_user_id: user_id }));
}

export async function updateShiftFacilitator(shift_team_id: string, user_id: string) {
  const clientInstance = await getClient();
  await clientInstance.request(updateItems(shift_collection_name, [shift_team_id], { facilitator_user_id: user_id }));
}

export async function removeShiftInterviewer(shift_team_id: string) {
  const clientInstance = await getClient();
  await clientInstance.request(updateItem(shift_team_collection_name, shift_team_id, { interviewer_user_id: null }));
}

export async function removeShiftAssistant(shift_team_id: string) {
  const clientInstance = await getClient();
  await clientInstance.request(updateItem(shift_team_collection_name, shift_team_id, { assistant_user_id: null }));
}

export async function getMyShifts(userId: string): Promise<MyShift[]> {
  const nowISO = new Date().toISOString();
  const clientInstance = await getClient();

  const teamRows = await
    clientInstance.request(readItems(shift_collection_name, {
      fields: [
        "id", "mode", "link", "start_date_utc", "end_date_utc",
        "place_id.id", "place_id.name",
        "city_id.id", "city_id.name",
        "facilitator_user_id.id",
        "facilitator_user_id.first_name",
        "facilitator_user_id.last_name",
        "facilitator_user_id.cod_curso", 
        "shift_team_shift.*"
      ] as any,
      filter: {
        _and: [
          {
            _or: [
              {
                shift_team_shift: {
                  interviewer_user_id: {
                    _eq: userId
                  }
                }
              },
              {
                shift_team_shift: {
                  assistant_user_id: {
                    _eq: userId
                  }
                }
              },
              {
                facilitator_user_id: {
                  _eq: userId
                }
              }
            ]
          },
          {
            start_date_utc: { _gte: nowISO }
          }
        ]
      } as any,
      sort: ["start_date_utc"]
    }));

  const teamShifts: MyShift[] = (teamRows as any[]).flatMap((row) => {
    const facilitatorId = getUserIdFromRelation(row.facilitator_user_id);
    const isFacilitator = facilitatorId === userId;
    const placeName = row.place_id?.name ?? row.city_id?.name ?? null;
    const isOnline = row.mode?.toLowerCase() === "online";

    if (isFacilitator) {
      return [{
        id: row.id,
        shiftId: row.id,
        role: "facilitador",
        mode: row.mode ?? "",
        place: isOnline ? "ON-LINE" : (placeName ?? ""),
        startDate: new Date(row.start_date_utc),
        endDate: new Date(row.end_date_utc),
        link: isOnline ? (row.link ?? undefined) : undefined,
        facilitator: row.facilitator_user_id ?? null
      } as MyShift];
    }

    const matchedTeams = (row.shift_team_shift ?? []).filter((team: any) => {
      const interviewerId = getUserIdFromRelation(team.interviewer_user_id);
      const assistantId = getUserIdFromRelation(team.assistant_user_id);
      return interviewerId === userId || assistantId === userId;
    });

    return matchedTeams.map((shiftTeam: any, index: number) => {
      const isInterviewer = getUserIdFromRelation(shiftTeam?.interviewer_user_id) === userId;

      return {
        id: shiftTeam?.id ?? `${row.id}-${isInterviewer ? "entrevistador" : "assistente"}-${index}`,
        shiftId: row.id,
        role: isInterviewer ? "entrevistador" : "assistente",
        mode: row.mode ?? "",
        place: isOnline ? "ON-LINE" : (placeName ?? ""),
        startDate: new Date(shiftTeam?.start_date_utc ?? row.start_date_utc),
        endDate: new Date(shiftTeam?.end_date_utc ?? row.end_date_utc),
        link: isOnline ? (row.link ?? undefined) : undefined,
        facilitator: row.facilitator_user_id ?? null
      } as MyShift;
    });
  });

  return teamShifts;
}
